/**
 * Frontend mirror of builder-service `REQUIRED_CONFIG_FIELDS`
 * (`src/modules/builder-service/src/services/cli/CliAnalyticsService.ts:57-73`).
 * "Enabled" alone is not "configured": `getDefaultConfig()` yields `{ pixelId: '' }`
 * (analytics.types.ts:481). A jest test asserts equality with the server literal.
 */
import type { AnalyticsIntegration, AnalyticsPlatform } from '../types/analytics.types';

export const REQUIRED_CONFIG_FIELDS: Readonly<Record<AnalyticsPlatform, readonly string[]>> = {
  segmently_internal: [],
  amplitude: ['apiKey'],
  ga4: ['measurementId'],
  gtm: ['containerId'],
  mixpanel: ['projectToken'],
  posthog: ['projectToken'],
  facebook_pixel: ['pixelId'],
  tiktok_pixel: ['pixelId'],
  custom_script: ['name'],
  facebook_capi: ['pixelId', 'accessToken'],
  ga4_measurement_protocol: ['measurementId', 'apiSecret'],
  amplitude_server: ['apiKey'],
  mixpanel_server: ['projectToken', 'projectId', 'serviceAccountUsername', 'serviceAccountSecret'],
  posthog_server: ['projectToken'],
  tiktok_events_api: ['pixelCode', 'accessToken'],
};

export function hasNonEmptyValue(value: unknown): boolean {
  if (typeof value === 'string') return value.trim().length > 0;
  return value !== undefined && value !== null;
}

export function isIntegrationConfigured(
  integration: Pick<AnalyticsIntegration, 'platform' | 'config'>,
  required: typeof REQUIRED_CONFIG_FIELDS = REQUIRED_CONFIG_FIELDS,
): boolean {
  const config = (integration.config ?? {}) as unknown as Record<string, unknown>;
  if (integration.platform === 'custom_script') {
    return hasNonEmptyValue(config.headScript) || hasNonEmptyValue(config.bodyScript);
  }
  const fields = required[integration.platform] ?? [];
  return fields.every((field) => hasNonEmptyValue(config[field]));
}

export function isEnabledAndConfigured(
  integration: AnalyticsIntegration,
  required: typeof REQUIRED_CONFIG_FIELDS = REQUIRED_CONFIG_FIELDS,
): boolean {
  return integration.enabled === true && isIntegrationConfigured(integration, required);
}
